import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MailIcon,
  LockIcon,
  UserIcon,
  PhoneIcon,
  QrCodeIcon,
  ArrowLeftIcon,
  LoaderIcon,
  CheckCircle2Icon,
  AlertCircleIcon,
  EyeIcon,
  EyeOffIcon,
  BuildingIcon
} from 'lucide-react';
import { thingsboardService } from '../services/thingsboard';

interface SignUpScreenProps {
  onBackToLogin: () => void;
}

export function SignUpScreen({ onBackToLogin }: SignUpScreenProps) {
  // Form states
  const [customerCode, setCustomerCode] = useState(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      return (
        searchParams.get('customer') ||
        searchParams.get('customerCode') ||
        searchParams.get('code') ||
        searchParams.get('customerId') ||
        ''
      ).trim();
    } catch {
      return '';
    }
  });
  const [customerTitle, setCustomerTitle] = useState<string | null>(null);
  const [isValidatingCustomer, setIsValidatingCustomer] = useState(false);
  const [customerValidateError, setCustomerValidateError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Status & Feedback
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Check URL parameters for QR scan auto-fill (e.g. ?customer=UUID or ?code=UUID)
  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const urlCode =
        searchParams.get('customer') ||
        searchParams.get('customerCode') ||
        searchParams.get('code') ||
        searchParams.get('customerId') ||
        '';
      if (urlCode) {
        setCustomerCode(urlCode.trim());
      }
    } catch {
      // Ignore URL parse error
    }
  }, []);

  // Validate Customer Code when it changes
  useEffect(() => {
    let active = true;
    async function validateCode() {
      const trimmed = customerCode.trim();
      if (!trimmed) {
        setCustomerTitle(null);
        setCustomerValidateError('');
        return;
      }

      // UUID format check (length >= 20)
      if (trimmed.length < 20) {
        setCustomerTitle(null);
        setCustomerValidateError('올바른 공간 코드(UUID) 형식이 아닙니다.');
        return;
      }

      setIsValidatingCustomer(true);
      setCustomerValidateError('');

      try {
        const cust = await thingsboardService.getCustomerById(trimmed);
        if (!active) return;

        if (cust && (cust.title || cust.name)) {
          setCustomerTitle(cust.title || cust.name);
          setCustomerValidateError('');
        } else {
          setCustomerTitle(null);
          setCustomerValidateError('등록되지 않은 공간 코드입니다.');
        }
      } catch (err) {
        if (!active) return;
        setCustomerTitle(null);
        setCustomerValidateError('공간 코드를 확인하는 중 오류가 발생했습니다.');
      } finally {
        if (active) setIsValidatingCustomer(false);
      }
    }

    const timer = setTimeout(validateCode, 50);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [customerCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedCode = customerCode.trim();
    if (!trimmedCode) {
      setError('공간 코드(Customer Code)를 입력해주세요.');
      return;
    }
    if (!customerTitle && customerValidateError) {
      setError('유효한 공간 코드를 입력해주세요.');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('올바른 이메일 주소를 입력해주세요.');
      return;
    }
    if (!name.trim()) {
      setError('이름(실명)을 입력해주세요.');
      return;
    }
    if (!password || password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setIsSubmitting(true);
    try {
      await thingsboardService.registerCustomerUser({
        email: email.trim(),
        password: password,
        firstName: name.trim(),
        customerId: trimmedCode,
        phoneNumber: phoneNumber.trim(),
        customerTitle: customerTitle || '공간'
      });

      setIsSuccess(true);
    } catch (err: any) {
      setError(err.message || '회원가입 요청 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-[100dvh] max-w-md mx-auto w-full flex flex-col justify-center px-5 py-8 overflow-y-auto relative font-sans select-none"
      style={{ background: 'var(--bg-space, #0a0f1a)', color: 'var(--mobile-text-primary, #f8fafc)' }}
    >
      {/* Background Grid */}
      <div
        className="fixed inset-0 pointer-events-none opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative z-10 w-full"
      >
        {/* Top Back Button */}
        <button
          onClick={onBackToLogin}
          type="button"
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 mb-6 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>로그인으로 돌아가기</span>
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-white mb-1.5 flex items-center gap-2">
            <span>회원가입</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-medium">
              SED
            </span>
          </h1>
          <p className="text-xs text-slate-400 leading-relaxed">
            소속 공간 코드를 입력하고 정보를 입력해주세요.<br />
            관리자 사용 승인 후 정상 로그인이 가능합니다.
          </p>
        </div>

        {/* Success Modal View */}
        <AnimatePresence>
          {isSuccess ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 rounded-2xl border border-emerald-500/30 bg-emerald-950/30 backdrop-blur-xl text-center space-y-4 shadow-2xl"
            >
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto ring-8 ring-emerald-500/10">
                <CheckCircle2Icon className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-white">가입 신청 완료</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  회원가입이 성공적으로 접수되었습니다.<br />
                  <b className="text-emerald-400">관리자가 사용 승인 및 공간 권한을 지정</b>한 후 로그인하실 수 있습니다.
                </p>
              </div>
              <button
                onClick={onBackToLogin}
                type="button"
                className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-all shadow-lg shadow-emerald-500/20"
              >
                로그인 화면으로 이동
              </button>
            </motion.div>
          ) : (
            /* Signup Form Card */
            <form
              onSubmit={handleSubmit}
              className="p-6 rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl space-y-4 shadow-2xl"
            >
              {/* Error Notice */}
              {error && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs flex items-center gap-2">
                  <AlertCircleIcon className="w-4 h-4 flex-shrink-0 text-rose-400" />
                  <span>{error}</span>
                </div>
              )}

              {/* 1. Customer Code (UUID) Input with QR support */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <QrCodeIcon className="w-3.5 h-3.5 text-cyan-400" />
                    <span>소속 공간 코드 (Customer Code)</span>
                  </span>
                  {customerTitle && (
                    <span className="text-[0.68rem] text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2Icon className="w-3 h-3" />
                      <span>{customerTitle}</span>
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="예: 2370eae0-f75c-11f0-9b2a-09c76e2d22ef"
                    value={customerCode}
                    onChange={(e) => setCustomerCode(e.target.value)}
                    className={`w-full h-11 pl-3.5 pr-9 rounded-xl border text-xs font-mono transition-all placeholder:text-slate-600 focus:outline-none focus:ring-1 ${
                      customerTitle
                        ? 'border-emerald-500/60 bg-emerald-950/20 text-emerald-300 focus:border-emerald-400 focus:ring-emerald-400'
                        : customerValidateError
                        ? 'border-rose-500/60 bg-rose-950/20 text-rose-300 focus:border-rose-400 focus:ring-rose-400'
                        : 'border-white/10 bg-black/40 text-slate-200 focus:border-cyan-400 focus:ring-cyan-400'
                    }`}
                  />
                  {isValidatingCustomer && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400">
                      <LoaderIcon className="w-4 h-4 animate-spin" />
                    </div>
                  )}
                </div>
                {customerValidateError && !isValidatingCustomer && (
                  <p className="text-[0.68rem] text-rose-400">{customerValidateError}</p>
                )}
                <p className="text-[0.65rem] text-slate-500 leading-tight">
                  * 발급된 공간 QR 코드로 접속 시 자동 입력되거나, 관리자로부터 전달받은 UUID 코드를 입력하세요.
                </p>
              </div>

              {/* 2. Name */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <UserIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span>이름 (실명)</span>
                </label>
                <input
                  type="text"
                  placeholder="홍길동"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                />
              </div>

              {/* 3. Email (Username) */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <MailIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span>이메일 주소 (로그인 ID)</span>
                </label>
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                />
              </div>

              {/* 4. Phone Number (Optional) */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <PhoneIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span>연락처 (선택)</span>
                </label>
                <input
                  type="tel"
                  placeholder="010-1234-5678"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                />
              </div>

              {/* 5. Password */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <LockIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span>비밀번호 (6자 이상)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-11 pl-3.5 pr-10 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  >
                    {showPassword ? <EyeOffIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* 6. Password Confirm */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <LockIcon className="w-3.5 h-3.5 text-cyan-400" />
                  <span>비밀번호 확인</span>
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  className="w-full h-11 px-3.5 rounded-xl border border-white/10 bg-black/40 text-slate-200 text-xs focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all placeholder:text-slate-600"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || (customerValidateError !== '' && customerCode.length > 0)}
                className="w-full h-11 mt-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold text-xs flex items-center justify-center gap-2 transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <LoaderIcon className="w-4 h-4 animate-spin" />
                    <span>가입 요청 중...</span>
                  </>
                ) : (
                  <span>회원가입</span>
                )}
              </button>
            </form>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
